import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  enqueueRegisterTasks,
  enqueueWarmTasks,
  fetchAllAccounts,
  listWarmRuns,
  type RelayConfig,
} from '../api/client';
import type { AccountRecord, WarmRun } from '../api/types';
import { Avatar } from '../components/Avatar';
import { IconBolt } from '../components/Icon';
import { StatusPill } from '../components/StatusPill';

interface Props {
  cfg: RelayConfig;
}

const HOUR = 1000 * 60 * 60;

type FilterKind = 'cold24' | 'all_active' | 'paused';

const FILTERS: { id: FilterKind; label: string; describe: string }[] = [
  {
    id: 'cold24',
    label: 'Not warmed in 24h',
    describe: 'Active accounts with last_finished_at older than 24 hours.',
  },
  {
    id: 'all_active',
    label: 'All active',
    describe: 'Every account whose status is "active".',
  },
  {
    id: 'paused',
    label: 'Paused',
    describe: 'Accounts whose last warming run hit a challenge.',
  },
];

export function Warming({ cfg }: Props) {
  const [records, setRecords] = useState<AccountRecord[] | null>(null);
  const [runs, setRuns] = useState<WarmRun[] | null>(null);
  const [filter, setFilter] = useState<FilterKind>('cold24');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [registerCount, setRegisterCount] = useState(1);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.url, cfg.token]);

  async function refresh() {
    setErr(null);
    try {
      const [recs, r] = await Promise.all([
        fetchAllAccounts(cfg),
        listWarmRuns(cfg, { limit: 200 }),
      ]);
      setRecords(recs);
      setRuns(r.runs);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  const filterRecords = useMemo(
    () => (kind: FilterKind): AccountRecord[] => {
      const all = records ?? [];
      if (kind === 'all_active') {
        return all.filter((r) => (r.status ?? 'active') === 'active');
      }
      if (kind === 'paused') {
        return all.filter((r) => r.warming?.last_status === 'paused');
      }
      return all.filter((r) => {
        if ((r.status ?? 'active') !== 'active') return false;
        const last = r.warming?.last_finished_at;
        if (!last) return true;
        return Date.now() - new Date(last).getTime() > 24 * HOUR;
      });
    },
    [records],
  );

  async function submitRegister() {
    const n = Math.max(1, Math.min(100, Math.floor(registerCount)));
    setBusy(true);
    setErr(null);
    try {
      const reason = `dashboard-plan-${new Date().toISOString().slice(0, 10)}`;
      const res = await enqueueRegisterTasks(cfg, n, reason);
      setToast(
        `Queued ${res.count} register task${res.count === 1 ? '' : 's'} — ` +
          'consumer picks up within ~5 min',
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setTimeout(() => setToast(null), 5000);
    }
  }

  async function trigger() {
    const usernames = filterRecords(filter).map((r) => r.username);
    if (usernames.length === 0) {
      setToast('Nothing to trigger.');
      setTimeout(() => setToast(null), 3000);
      return;
    }
    setBusy(true);
    try {
      const res = await enqueueWarmTasks(cfg, usernames);
      const parts = [
        `Queued ${res.created} warm task${res.created === 1 ? '' : 's'}`,
      ];
      if (res.failed.length > 0) {
        parts.push(`${res.failed.length} failed`);
      }
      parts.push('— consumer picks up within ~5 min');
      setToast(parts.join(' '));
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setTimeout(() => setToast(null), 5000);
    }
  }

  const previewCount = records ? filterRecords(filter).length : 0;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header>
        <h1 className="text-xl font-semibold tracking-tight">Warming</h1>
        <p className="mt-0.5 text-sm text-[var(--color-text-dim)]">
          Push warming tasks onto the Cloudflare Queue. The consumer
          (<code className="text-zinc-300">auto-reddit-consumer drain</code>{' '}
          running on your host) pulls one every ~5 min and runs a full
          orchestration cycle (~15-20 min per account).
        </p>
      </header>

      {/* Register today */}
      <section className="card p-5">
        <h2 className="text-sm font-medium text-zinc-200">Register today</h2>
        <p className="mt-1 text-xs text-[var(--color-text-dim)]">
          Push N register tasks to the Cloudflare queue. The consumer
          (<code className="text-zinc-300">drain-register</code> on the
          host) reserves one eligible proxy per task (1 account per
          proxy per 24h); excess tasks wait via visibility timeout.
        </p>
        <div className="mt-3 flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-[var(--color-text-dim)]">
            How many?
            <input
              type="number"
              min={1}
              max={100}
              value={registerCount}
              onChange={(e) => setRegisterCount(parseInt(e.target.value, 10) || 1)}
              className="input w-20 text-center"
            />
          </label>
          <button
            onClick={submitRegister}
            disabled={busy}
            className="btn-primary"
          >
            <IconBolt width={14} height={14} />
            {busy ? 'Queuing…' : `Queue ${registerCount} register${registerCount === 1 ? '' : 's'}`}
          </button>
        </div>
      </section>

      {/* Bulk trigger */}
      <section className="card p-5">
        <h2 className="text-sm font-medium text-zinc-200">Bulk trigger warming</h2>
        <div className="mt-3 grid gap-2 md:grid-cols-3">
          {FILTERS.map((f) => {
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`rounded-md border p-3 text-left transition ${
                  active
                    ? 'border-emerald-500/40 bg-emerald-500/5 ring-1 ring-emerald-500/20'
                    : 'border-[var(--color-border)] bg-[var(--color-surface-2)]/30 hover:border-[var(--color-border-strong)]'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-zinc-100">
                    {f.label}
                  </div>
                  <span
                    className={`h-3.5 w-3.5 rounded-full border-2 transition ${
                      active
                        ? 'border-emerald-400 bg-emerald-400/40'
                        : 'border-[var(--color-border-strong)]'
                    }`}
                  />
                </div>
                <div className="mt-1 text-xs text-[var(--color-text-dim)]">
                  {f.describe}
                </div>
              </button>
            );
          })}
        </div>
        <div className="mt-4 flex items-center justify-between border-t border-[var(--color-border)] pt-4">
          <div className="text-sm text-[var(--color-text-dim)]">
            {records ? (
              <>
                <span className="text-zinc-100 tabular-nums">{previewCount}</span>{' '}
                accounts match
              </>
            ) : (
              'Loading roster…'
            )}
          </div>
          <button
            onClick={trigger}
            disabled={busy || previewCount === 0}
            className="btn-primary"
          >
            <IconBolt width={14} height={14} />
            {busy ? 'Queuing…' : 'Queue warming'}
          </button>
        </div>
      </section>

      {toast && (
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">
          {toast}
        </div>
      )}
      {err && (
        <div className="rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">
          {err}
        </div>
      )}

      <div className="grid gap-6">
        <section className="card overflow-hidden">
          <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-3">
            <span className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-faint)]">
              Recent runs
            </span>
            <span className="text-xs tabular-nums text-[var(--color-text-faint)]">
              {runs?.length ?? 0}
            </span>
          </div>
          {!runs && <div className="p-6 text-sm text-[var(--color-text-dim)]">Loading…</div>}
          {runs && runs.length === 0 && (
            <div className="p-8 text-center text-sm text-[var(--color-text-dim)]">
              No runs recorded yet.
            </div>
          )}
          {runs && runs.length > 0 && (
            <ul className="max-h-[480px] divide-y divide-[var(--color-border)] overflow-y-auto">
              {runs.map((r) => {
                const ok = r.activities.filter((a) => a.ok).length;
                return (
                  <li
                    key={`${r.username}:${r.started_at}`}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm"
                  >
                    <Avatar name={r.username} />
                    <Link
                      to={`/accounts/${encodeURIComponent(r.username)}`}
                      className="min-w-0 flex-1 truncate text-zinc-200 hover:text-emerald-300"
                    >
                      {r.username}
                    </Link>
                    <StatusPill status={r.status} />
                    <span className="tabular-nums text-xs text-[var(--color-text-faint)]">
                      {ok}/{r.activities.length}
                    </span>
                    <span className="inline-flex items-center gap-0.5 tabular-nums text-xs text-emerald-300">
                      <IconBolt width={10} height={10} />
                      {r.upvotes_cast}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
